import { FolderPlus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';

/** The trigger, for the Repos header, beside enrolling a device on Agents. */
export function AddRepoButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick}>
      <FolderPlus aria-hidden />
      Register a repo
    </Button>
  );
}

/**
 * Registering a repo, in place (issue #101).
 *
 * The one door that used to not exist: a repo became real in Dyspatch the
 * moment somebody typed its name into a job, and a typo produced a job no
 * worker would ever pick up. The hub checks the slug against GitHub before
 * accepting it, so the refusal a typo earns now happens here, at the one
 * field, rather than being discovered later as a job that never dispatched.
 *
 * One field, unlike the person and device composers this otherwise matches:
 * there is no secret to show afterwards, so the form simply closes and toasts
 * on success rather than switching to a second phase.
 */
export function AddRepoComposer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [slug, setSlug] = useState('');
  const first = useRef<HTMLInputElement>(null);

  const create = useHubAction(
    async () => {
      const clean = slug.trim();
      const result = await api.createRepo(clean);
      return result;
    },
    {
      success: () => `${slug.trim()} registered. It can be picked when composing a job.`,
      onDone: () => {
        onClose();
        setSlug('');
      },
      // The form stays open on a refusal (a typo GitHub does not recognise, or
      // a slug already registered) and prints it under its own fields.
      inlineError: true,
    },
  );

  useEffect(() => {
    if (open) first.current?.focus();
  }, [open]);

  const close = () => {
    onClose();
    setSlug('');
  };

  if (!open) return null;

  return (
    <section className="bg-card overflow-hidden rounded-lg border">
      <div className="bg-sunken flex items-center gap-2 border-b px-gutter py-[calc(var(--dy-row-py)+1px)]">
        <h2 className="text-sm font-medium">Register a repo</h2>
        <span className="text-fg-3 text-2xs">checked against GitHub before it is added</span>
        <Button
          variant="ghost"
          size="icon-xs"
          className="ml-auto"
          onClick={close}
          aria-label="Close"
        >
          <X />
        </Button>
      </div>

      <form
        className="space-y-2.5 px-gutter py-3"
        onKeyDown={(event) => {
          if (event.key === 'Escape') close();
        }}
        onSubmit={(event) => {
          event.preventDefault();
          create.run();
        }}
      >
        <div className="max-w-sm space-y-1">
          <Label htmlFor="ar-slug">Repo</Label>
          <Input
            ref={first}
            id="ar-slug"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            required
            minLength={1}
            maxLength={200}
            placeholder="nymbl-app"
          />
          <p className="text-fg-3 text-2xs">
            The bare name, the way a job already names it. Enabled by default.
          </p>
        </div>

        {create.error ? (
          <p className="text-bad text-xs" role="alert">
            {create.error}
          </p>
        ) : null}

        <div className="flex items-center gap-1.5">
          <Button type="submit" size="sm" disabled={create.busy}>
            {create.busy ? 'Checking…' : 'Register'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={close}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}
